import React from "react";
import FormWrapper from "../shared/FormWrapper";
import ProgressBar from "../shared/ProgressBar";
import AutoSaveIndicator from "../shared/AutoSaveIndicator";

export default function ProfileBasics({
  formData,
  onInputChange,
  onImageUpload,
  imagePreview,
  uploadingImage,
  onContinue,
  onBack,
  onSaveAndExit,
  saving,
  lastSaved,
}) {
  // Default profile placeholder component
  const DefaultProfileIcon = () => (
    <div
      style={{
        width: "120px",
        height: "120px",
        borderRadius: "50%",
        backgroundColor: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "3px solid #fff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      }}
    >
      <svg
        width="60"
        height="60"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#d1d5db"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    </div>
  );

  return (
    <FormWrapper>
      {/* Progress Bar */}
      <ProgressBar currentStep={1} totalSteps={5} />

      {/* Icon and Title */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "32px",
          animation: "fadeInUp 0.5s ease",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            backgroundColor: "#f0fdf4",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            border: "1px solid #d1fae5",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>

        <h1
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "8px",
            letterSpacing: "-0.5px",
          }}
        >
          Let's start with the basics
        </h1>
        <p
          style={{
            fontSize: "15px",
            color: "#737373",
            margin: 0,
          }}
        >
          Help churches get to know you
        </p>
      </div>

      {/* Profile Picture Upload */}
      <div
        style={{
          marginBottom: "32px",
          textAlign: "center",
          animation: "fadeInUp 0.5s ease 0.1s both",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "120px",
            height: "120px",
            margin: "0 auto 12px",
          }}
        >
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Profile"
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid #fff",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                transition: "transform 0.2s ease",
              }}
            />
          ) : (
            <DefaultProfileIcon />
          )}
          <label
            style={{
              position: "absolute",
              bottom: "4px",
              right: "4px",
              width: "36px",
              height: "36px",
              backgroundColor: "#1a1a1a",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.backgroundColor = "#333";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.backgroundColor = "#1a1a1a";
            }}
          >
            <input
              type="file"
              accept="image/*"
              onChange={onImageUpload}
              style={{ display: "none" }}
              disabled={uploadingImage}
            />
            {uploadingImage ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                style={{ animation: "spin 1s linear infinite" }}
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  strokeDasharray="32"
                  strokeDashoffset="12"
                />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            )}
          </label>
        </div>
        <p style={{ fontSize: "13px", color: "#a3a3a3", margin: 0 }}>
          {uploadingImage ? "Uploading..." : "Add a profile photo"}
        </p>
      </div>

      {/* Name Input */}
      <div
        style={{
          marginBottom: "20px",
          animation: "fadeInUp 0.5s ease 0.2s both",
        }}
      >
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Your name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => onInputChange("name", e.target.value)}
          placeholder="How should churches address you?"
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            color: "#1a1a1a",
            backgroundColor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            outline: "none",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#1a1a1a";
            e.currentTarget.style.boxShadow =
              "0 0 0 3px rgba(26, 26, 26, 0.05)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e5e5";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
      </div>

      {/* Location Input */}
      <div
        style={{
          marginBottom: "32px",
          animation: "fadeInUp 0.5s ease 0.3s both",
        }}
      >
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          City or town
        </label>
        <div style={{ position: "relative" }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#a3a3a3"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              position: "absolute",
              left: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => onInputChange("location", e.target.value)}
            placeholder="Where are you based?"
            style={{
              width: "100%",
              padding: "12px 16px 12px 44px",
              fontSize: "16px",
              color: "#1a1a1a",
              backgroundColor: "#fff",
              border: "1px solid #e5e5e5",
              borderRadius: "8px",
              outline: "none",
              transition: "all 0.2s ease",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#1a1a1a";
              e.currentTarget.style.boxShadow =
                "0 0 0 3px rgba(26, 26, 26, 0.05)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#e5e5e5";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </div>
        <p style={{ fontSize: "13px", color: "#a3a3a3", marginTop: "8px" }}>
          This helps churches find help locally
        </p>
      </div>

      {/* Short Bio / Service Title */}
      <div
        style={{
          marginBottom: "32px",
          animation: "fadeInUp 0.5s ease 0.4s both",
        }}
      >
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px",
          }}
        >
          Short bio
          <span style={{ color: "#a3a3a3", fontWeight: "400", marginLeft: "6px" }}>(optional)</span>
        </label>
        <input
          type="text"
          value={formData.skillTitle || ""}
          onChange={(e) => onInputChange("skillTitle", e.target.value)}
          placeholder="E.g., Worship leader & songwriter with 10 years experience"
          style={{
            width: "100%",
            padding: "12px 16px",
            fontSize: "16px",
            color: "#1a1a1a",
            backgroundColor: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            outline: "none",
            transition: "all 0.2s ease",
            boxSizing: "border-box",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = "#1a1a1a";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(26, 26, 26, 0.05)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = "#e5e5e5";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
        <p style={{ fontSize: "13px", color: "#a3a3a3", marginTop: "8px" }}>
          A one-liner that appears on your profile card
        </p>
      </div>

      {/* Auto-save indicator */}
      <AutoSaveIndicator saving={saving} lastSaved={lastSaved} />

      {/* Continue Button - Dark style matching site */}
      <button
        onClick={onContinue}
        disabled={!formData.name.trim()}
        style={{
          width: "100%",
          padding: "16px 24px",
          fontSize: "16px",
          fontWeight: "600",
          color: "#fff",
          backgroundColor: formData.name.trim() ? "#1a1a1a" : "#d4d4d4",
          border: "none",
          borderRadius: "8px",
          cursor: formData.name.trim() ? "pointer" : "not-allowed",
          transition: "all 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
        onMouseEnter={(e) => {
          if (formData.name.trim()) {
            e.currentTarget.style.backgroundColor = "#333";
            e.currentTarget.style.transform = "translateY(-1px)";
          }
        }}
        onMouseLeave={(e) => {
          if (formData.name.trim()) {
            e.currentTarget.style.backgroundColor = "#1a1a1a";
            e.currentTarget.style.transform = "translateY(0)";
          }
        }}
      >
        Continue
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>

      {/* Save & Exit - subtle link below */}
      <button
        onClick={onSaveAndExit}
        style={{
          width: "100%",
          padding: "12px",
          marginTop: "12px",
          fontSize: "14px",
          fontWeight: "500",
          color: "#a3a3a3",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "color 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#737373")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#a3a3a3")}
      >
        Save & finish later
      </button>
    </FormWrapper>
  );
}
